'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AIModel } from '@repo/shared';
import { ChevronDown, ImagePlus, Image as ImageIcon, Music, Video, MessageCircle } from 'lucide-react';
import { cn } from '../utils/cn';
import { makeSvgIdsUnique } from '../utils/svg';

export type GeneratorBarGenerationType = 'image' | 'video' | 'music' | 'chat';

const dropdownContainerClass =
  'bg-[#27282b] rounded-2xl border border-white/5 backdrop-blur-[39px] shadow-[0_4px_47px_rgba(0,0,0,0.75)] p-4 flex flex-col gap-2';

export function TypePanel({
  selected,
  onSelect,
  showMusic = false,
}: {
  selected: GeneratorBarGenerationType;
  onSelect: (type: GeneratorBarGenerationType) => void;
  showMusic?: boolean;
}) {
  const options: { type: GeneratorBarGenerationType; icon: typeof ImageIcon; label: string; desc: string }[] = [
    { type: 'image', icon: ImageIcon, label: 'Изображение', desc: 'Создать изображение с помощью ИИ' },
    { type: 'video', icon: Video, label: 'Видео', desc: 'Сгенерировать видео из изображения' },
    ...(showMusic
      ? [{ type: 'music' as const, icon: Music, label: 'Музыка', desc: 'Сгенерировать трек по описанию' }]
      : []),
    { type: 'chat', icon: MessageCircle, label: 'Чат', desc: 'Задать вопрос AI-ассистенту' },
  ];

  return (
    <div className={dropdownContainerClass}>
      {options.map((opt) => {
        const isActive = selected === opt.type;
        return (
          <button
            key={opt.type}
            onClick={() => onSelect(opt.type)}
            className={cn(
              'flex h-14 w-full items-center gap-[9px] rounded-[9px] px-2 text-left transition-colors',
              isActive && 'rounded-xl bg-accent',
            )}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center">
              <opt.icon className="h-[25px] w-[25px] text-white" />
            </div>
            <div className="flex flex-1 flex-col">
              <span className="text-base font-normal leading-[25px] tracking-tight text-white">
                {opt.label}
              </span>
              <span className={cn('text-[10px] leading-[13px]', isActive ? 'text-white' : 'text-[#8e8e93]')}>
                {opt.desc}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Group input models by familyKey while preserving the existing
// sortOrder for singletons and family parents. Within a family,
// variants are sorted by familyOrder DESC (newest first), with
// undefined familyOrder treated as 0. The first variant encountered
// (by sortOrder) sets the family's position in the resulting list.
type ModelGroup =
  | { kind: 'single'; model: AIModel }
  | { kind: 'family'; key: string; name: string; members: AIModel[] };

function groupModelsByFamily(models: AIModel[]): ModelGroup[] {
  const groups: ModelGroup[] = [];
  const familyIndex = new Map<string, number>();

  for (const model of models) {
    const key = model.familyKey || null;
    if (!key) {
      groups.push({ kind: 'single', model });
      continue;
    }
    const existing = familyIndex.get(key);
    if (existing !== undefined) {
      const slot = groups[existing];
      if (slot && slot.kind === 'family') slot.members.push(model);
      continue;
    }
    familyIndex.set(key, groups.length);
    groups.push({
      kind: 'family',
      key,
      name: model.familyName || model.name,
      members: [model],
    });
  }

  for (const group of groups) {
    if (group.kind !== 'family') continue;
    group.members.sort((a, b) => (b.familyOrder ?? 0) - (a.familyOrder ?? 0));
  }

  return groups;
}

export function ModelPanel({
  models,
  selectedSlug,
  onSelect,
}: {
  models: AIModel[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  const groups = useMemo(() => groupModelsByFamily(models), [models]);

  // Accordion: only one family expanded at a time. On open / on
  // selectedSlug change, default to the family that contains the
  // current selection so the user can immediately see what's picked.
  const familyOfSelected = useMemo(() => {
    if (!selectedSlug) return null;
    for (const group of groups) {
      if (group.kind !== 'family') continue;
      if (group.members.some((m) => m.slug === selectedSlug)) return group.key;
    }
    return null;
  }, [groups, selectedSlug]);

  const [expandedKey, setExpandedKey] = useState<string | null>(familyOfSelected);
  // If the selected model changes from outside (e.g. sidebar nav), keep
  // the accordion in sync by re-pointing at the family of the new pick.
  useEffect(() => {
    if (familyOfSelected) setExpandedKey(familyOfSelected);
  }, [familyOfSelected]);

  if (!models.length) {
    return (
      <div className={dropdownContainerClass}>
        <p className="text-center text-sm text-[#8e8e93]">Загрузка моделей...</p>
      </div>
    );
  }

  return (
    <div className={cn(dropdownContainerClass, 'max-h-[600px] overflow-y-auto overscroll-contain scrollbar-hide')}>
      <p className="px-2 pb-1 text-xs text-[#8e8e93]">Модель</p>
      {groups.map((group) => {
        if (group.kind === 'single') {
          return (
            <ModelRow
              key={group.model.slug}
              model={group.model}
              isActive={selectedSlug === group.model.slug}
              onSelect={() => onSelect(group.model.slug)}
            />
          );
        }
        const isOpen = expandedKey === group.key;
        return (
          <div key={group.key} className="flex flex-col">
            <button
              type="button"
              onClick={() => setExpandedKey(isOpen ? null : group.key)}
              className="flex w-full items-center gap-1.5 rounded-xl px-2 py-2.5 text-left transition-colors"
            >
              <ModelIcon model={group.members[0]!} idPrefix={`mp-${group.key}`} />
              <div className="flex flex-1 flex-col">
                <span className="text-base font-normal leading-[25px] tracking-tight text-white">
                  {group.name}
                </span>
                <span className="text-[10px] leading-[13px] text-[#8e8e93]">
                  {group.members.length} {pluralModels(group.members.length)}
                </span>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-[#8e8e93] transition-transform duration-200',
                  isOpen && 'rotate-180',
                )}
              />
            </button>
            {/* Smooth expand/collapse via the CSS grid `0fr → 1fr` trick:
             * the browser animates the implicit row size with a single
             * transition, no JS height measurement, no layout thrash even
             * when the family has many members. The chevron rotates in
             * sync. We always render the children so animating-out has
             * something to shrink. `pointer-events-none` while collapsed
             * keeps invisible rows from eating clicks. */}
            <div
              className={cn(
                'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                isOpen
                  ? 'grid-rows-[1fr] opacity-100'
                  : 'pointer-events-none grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <div className="ml-4 flex flex-col border-l border-white/[0.06] pl-2 pt-1">
                  {group.members.map((member) => (
                    <ModelRow
                      key={member.slug}
                      model={member}
                      isActive={selectedSlug === member.slug}
                      onSelect={() => onSelect(member.slug)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function pluralModels(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'модель';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'модели';
  return 'моделей';
}

function ModelIcon({ model, idPrefix }: { model: AIModel; idPrefix: string }) {
  if (model.iconSvg) {
    return (
      <div
        className="flex h-[21px] w-[21px] shrink-0 items-center justify-center overflow-hidden rounded-full [&>svg]:h-[21px] [&>svg]:w-[21px]"
        // Per-render id prefix so several gradient-using icons
        // (e.g. Gemini's inline `<linearGradient id="SVGID_1">`)
        // can coexist on the same screen without their refs
        // collapsing into one another and rendering blank.
        dangerouslySetInnerHTML={{ __html: makeSvgIdsUnique(model.iconSvg, idPrefix) }}
      />
    );
  }
  return <div className="h-[21px] w-[21px] shrink-0 rounded-full bg-[#555]" />;
}

function ModelRow({
  model,
  isActive,
  onSelect,
}: {
  model: AIModel;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-xl px-2 py-2.5 text-left transition-colors',
        isActive && 'bg-accent !px-2 !py-2',
      )}
    >
      <div className="flex items-center gap-1.5">
        <ModelIcon model={model} idPrefix={`mp-${model.slug}`} />
        <span className="text-base font-normal leading-[25px] tracking-tight text-white">
          {model.name}
        </span>
      </div>
      {model.description && (
        <span className={cn('text-[10px] leading-[13px]', isActive ? 'text-[#fafafa]' : 'text-[#8e8e93]')}>
          {model.description}
        </span>
      )}
      <div className="mt-0.5 flex gap-1.5">
        <ModelBadge label={getQualityLabel(model)} isActive={isActive} />
        <ModelBadge label={getSpeedLabel(model)} isActive={isActive} />
      </div>
    </button>
  );
}

function getQualityLabel(model: AIModel): string {
  const price = parseFloat(model.pricePerGeneration);
  if (price >= 25) return '4K';
  if (price >= 10) return 'HD';
  return 'SD';
}

function getSpeedLabel(model: AIModel): string {
  if (model.category === 'LLM') return 'Стрим';
  if (model.category === 'MUSIC') return '~2м';
  return model.category === 'VIDEO' ? '~30с' : '~10с';
}

function ModelBadge({ label, isActive }: { label: string; isActive: boolean }) {
  return (
    <span
      className={cn(
        'rounded-md px-2 py-0.5 text-[10px] font-medium leading-[13px]',
        isActive ? 'bg-white/20 text-white' : 'bg-white/[0.06] text-[#aeaeb2]',
      )}
    >
      {label}
    </span>
  );
}

export function RatioPanel({
  ratios,
  selected,
  onSelect,
  centered = false,
}: {
  ratios: string[];
  selected: string | null;
  onSelect: (ratio: string) => void;
  /**
   * Center the chips row inside the popover. Desktop-only — on mobile
   * the popover is narrow enough that chips need to be left-aligned with
   * `overflow-x-auto`, otherwise centered overflow hides the leftmost
   * items behind the popover edge.
   */
  centered?: boolean;
}) {
  const parseRatio = (r: string): { w: number; h: number } => {
    if (r === 'auto') return { w: 1, h: 1 };
    const parts = r.split(':').map(Number);
    return { w: parts[0] || 1, h: parts[1] || 1 };
  };

  const getPreviewSize = (r: string): { w: number; h: number } => {
    const { w, h } = parseRatio(r);
    const maxSize = 200;
    if (w >= h) {
      return { w: maxSize, h: Math.round(maxSize * (h / w)) };
    }
    return { w: Math.round(maxSize * (w / h)), h: maxSize };
  };

  const getRatioRect = (r: string) => {
    if (r === 'auto') {
      return { x: 6, y: 6, w: 42, h: 42 };
    }
    const { w, h } = parseRatio(r);
    const maxSize = 42;
    const padding = 6;
    let rw: number;
    let rh: number;
    if (w >= h) {
      rw = maxSize;
      rh = Math.round(maxSize * (h / w));
    } else {
      rh = maxSize;
      rw = Math.round(maxSize * (w / h));
    }
    const rx = padding + Math.round((maxSize - rw) / 2);
    const ry = padding + Math.round((maxSize - rh) / 2);
    return { x: rx, y: ry, w: rw, h: rh };
  };

  const effectiveSelected = selected || ratios[0] || '1:1';
  const preview = getPreviewSize(effectiveSelected);
  const isAuto = effectiveSelected === 'auto';

  return (
    <div className={cn(dropdownContainerClass, 'items-center gap-6')}>
      <span className="w-full text-center text-base font-normal leading-[25px] tracking-tight text-white">
        Соотношение сторон
      </span>

      <div
        className={cn(
          'relative overflow-visible transition-[width,height,opacity,background-color] duration-500 ease-in-out',
          isAuto ? 'bg-[#1a1a1c] opacity-30' : 'bg-black opacity-100',
        )}
        style={{ width: preview.w, height: preview.h }}
      >
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 300 300" preserveAspectRatio="none">
          <line x1="0" y1="100" x2="300" y2="100" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1="200" x2="300" y2="200" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          <line x1="100" y1="0" x2="100" y2="300" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          <line x1="200" y1="0" x2="200" y2="300" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className={cn('absolute -left-[10px] -top-[10px] h-5 w-5 rounded-[5px] transition-colors duration-500', isAuto ? 'bg-[#555]' : 'bg-white')} />
        <div className={cn('absolute -right-[10px] -top-[10px] h-5 w-5 rounded-[5px] transition-colors duration-500', isAuto ? 'bg-[#555]' : 'bg-white')} />
        <div className={cn('absolute -bottom-[10px] -left-[10px] h-5 w-5 rounded-[5px] transition-colors duration-500', isAuto ? 'bg-[#555]' : 'bg-white')} />
        <div className={cn('absolute -bottom-[10px] -right-[10px] h-5 w-5 rounded-[5px] transition-colors duration-500', isAuto ? 'bg-[#555]' : 'bg-white')} />
      </div>

      <div
        className={cn(
          'scrollbar-hide flex w-full gap-[5px] overflow-x-auto overflow-y-hidden px-0 pb-0 pt-5 touch-pan-x',
          centered && 'justify-center',
        )}
      >
        {ratios.map((ratio) => {
          const isActive = effectiveSelected === ratio;
          const rect = getRatioRect(ratio);
          return (
            <button
              key={ratio}
              onClick={() => onSelect(ratio)}
              className={cn(
                'relative flex h-[50px] w-[50px] min-w-[50px] flex-shrink-0 items-center justify-center border-none p-0',
                isActive ? 'rounded-xl bg-accent' : 'rounded-[10px] bg-transparent',
              )}
            >
              <svg className="pointer-events-none absolute -left-[2px] -top-[2px]" viewBox="0 0 54 54" width="54" height="54">
                <rect x="1" y="1" width="52" height="52" rx="3" fill="none" stroke="transparent" strokeWidth="2" />
                <rect
                  x={rect.x}
                  y={rect.y}
                  width={rect.w}
                  height={rect.h}
                  rx="6"
                  fill="none"
                  stroke={isActive ? 'rgba(255,255,255,0.5)' : '#4e4e50'}
                  strokeWidth="1"
                />
              </svg>
              <span className="relative z-[1] text-center text-xs font-normal leading-[21px] tracking-tight text-white">
                {ratio === 'auto' ? 'Авто' : ratio}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function OptionListPanel({
  title,
  options,
  selected,
  onSelect,
  suffix,
  labels,
}: {
  title: string;
  options: string[];
  selected: string | null;
  onSelect: (value: string) => void;
  suffix?: string;
  labels?: Record<string, string>;
}) {
  return (
    <div className={cn(dropdownContainerClass, 'max-h-[400px] overflow-y-auto overscroll-contain scrollbar-hide')}>
      <p className="px-2 pb-1 text-xs text-[#8e8e93]">{title}</p>
      {options.map((opt) => {
        const isActive = selected === opt;
        const display = labels?.[opt] || `${opt}${suffix || ''}`;
        return (
          <button
            key={opt}
            onClick={(e) => {
              e.nativeEvent.stopImmediatePropagation();
              onSelect(opt);
            }}
            className={cn(
              'flex h-11 w-full items-center rounded-xl px-4 text-left transition-colors',
              isActive && 'bg-accent',
            )}
          >
            <span className="text-base leading-[25px] tracking-tight text-white">{display}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SelectChip({
  label,
  bold,
  active,
  onClick,
  hideChevron = false,
  icon,
  ariaLabel,
}: {
  /** Optional — omit for icon-only chips (e.g. the audio on/off toggle). */
  label?: string;
  bold?: boolean;
  active?: boolean;
  onClick?: () => void;
  /** Drop the trailing chevron when the chip toggles state in place
   *  instead of opening a dropdown — e.g. the audio on/off toggle. */
  hideChevron?: boolean;
  /** Optional leading icon. When present without a label, the chip
   *  collapses to an icon-only square (extra horizontal padding
   *  trimmed). */
  icon?: React.ReactNode;
  /** Accessible name when the chip is icon-only. */
  ariaLabel?: string;
}) {
  const iconOnly = !!icon && !label;
  return (
    <div
      onClick={onClick}
      role={ariaLabel ? 'button' : undefined}
      aria-label={ariaLabel}
      className={cn(
        'flex cursor-pointer items-center rounded-[13px] border py-1.5 transition-colors',
        iconOnly ? 'gap-0 px-2.5' : hideChevron ? 'gap-1 px-3 pr-3' : 'gap-1 px-3 pr-2',
        active ? 'border-white/20 bg-white/10' : 'border-white/[0.11] bg-black/20',
      )}
    >
      {icon && (
        <span className="flex h-[18px] w-[18px] items-center justify-center text-white">
          {icon}
        </span>
      )}
      {label && (
        <span className={cn('max-w-[130px] truncate text-sm leading-[18px] text-white', bold && 'font-medium')}>
          {label}
        </span>
      )}
      {!hideChevron && (
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="#fff"
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn('transition-transform duration-200', active && 'rotate-180')}
        >
          <path d="M2.5 3.75L5 6.25L7.5 3.75" />
        </svg>
      )}
    </div>
  );
}

export function UploadChip({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="relative flex cursor-pointer items-center gap-1 rounded-[13px] border border-white/[0.11] bg-black/20 px-2.5 py-1.5"
    >
      <ImagePlus className="h-4 w-4 text-white" />
      <span className="text-sm leading-[18px] text-white">Фото</span>
      {count > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-medium text-white">
          {count}
        </span>
      )}
    </div>
  );
}

export function DurationSliderPanel({
  options,
  selected,
  onSelect,
}: {
  options: string[];
  selected: string | null;
  onSelect: (v: string) => void;
}) {
  const sorted = [...options].sort((a, b) => Number(a) - Number(b));
  const currentIndex = selected ? sorted.indexOf(selected) : 0;
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const sortedRef = useRef(sorted);
  sortedRef.current = sorted;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const handleInteraction = useCallback((clientX: number) => {
    const track = trackRef.current;
    const s = sortedRef.current;
    if (!track || s.length < 2) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const index = Math.round(ratio * (s.length - 1));
    const val = s[index];
    if (val && val !== selectedRef.current) onSelectRef.current(val);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    draggingRef.current = true;
    const t = e.touches[0];
    if (t) handleInteraction(t.clientX);
  }, [handleInteraction]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) handleInteraction(touch.clientX);
  }, [handleInteraction]);

  const handleTouchEnd = useCallback(() => {
    draggingRef.current = false;
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    handleInteraction(e.clientX);
  }, [handleInteraction]);

  const progress = sorted.length > 1 ? (currentIndex / (sorted.length - 1)) * 100 : 0;
  const animate = !draggingRef.current;

  return (
    <div className={cn(dropdownContainerClass, 'px-[22px] pb-[18px] pt-[13px]')}>
      <p className="mb-3 text-xs text-[#8e8e93]">Длительность</p>
      <div
        ref={trackRef}
        className="relative flex h-[36px] cursor-pointer touch-none items-center"
        onClick={handleClick}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="absolute left-0 right-0 h-[4px] rounded-full bg-white/10" />
        <div
          className="absolute left-0 h-[4px] origin-left rounded-full bg-accent will-change-transform"
          style={{
            transform: `scaleX(${progress / 100})`,
            width: '100%',
            transition: animate ? 'transform 120ms ease-out' : 'none',
          }}
        />
        {sorted.map((_, i) => {
          const pos = sorted.length > 1 ? (i / (sorted.length - 1)) * 100 : 0;
          return (
            <div
              key={i}
              className={cn(
                'absolute h-[8px] w-[8px] -translate-x-1/2 rounded-full',
                i <= currentIndex ? 'bg-accent' : 'bg-white/20',
              )}
              style={{ left: `${pos}%` }}
            />
          );
        })}
        <div
          className="absolute h-[22px] w-[22px] -translate-x-1/2 rounded-full bg-white shadow-md will-change-[left]"
          style={{
            left: `${progress}%`,
            transition: animate ? 'left 120ms ease-out' : 'none',
          }}
        />
      </div>
      <div className="relative mt-1 flex justify-between">
        {sorted.map((val, i) => (
          <span
            key={val}
            className={cn(
              'text-xs transition-colors',
              i === currentIndex ? 'font-semibold text-white' : 'text-[#8e8e93]',
            )}
            style={sorted.length > 2 ? {
              position: 'absolute',
              left: `${(i / (sorted.length - 1)) * 100}%`,
              transform: 'translateX(-50%)',
            } : undefined}
          >
            {val}с
          </span>
        ))}
      </div>
    </div>
  );
}
